import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControl, ListItemText, MenuItem,
  Paper, Select, TextField, Typography,
} from '@mui/material';
import { IoDownloadOutline, IoRefreshOutline } from 'react-icons/io5';
import PptxGenJS from 'pptxgenjs';
import { getLoggedInUser } from '../../utils/permissions';
import { getDateRangeParams } from '../../utils/dateRange';
import routesLogo from '../../assets/routes.png';

const SOURCES = [
  { workflow: 'Vehicle Damage', new: '/get_submitted_open_forms', processing: '/get_claim_processing_forms', closed: '/get_closed_forms' },
  { workflow: 'Windshield / Tire', new: '/get_submitted_windshield_tire_open_forms', processing: '/get_windshield_tire_claim_processing_forms', closed: '/get_closed_windshield_tire_forms' },
  { workflow: 'Shuttle Bus', new: '/get_submitted_shuttle_bus_open_forms', processing: '/get_shuttle_bus_claim_processing_forms', closed: '/get_closed_shuttle_bus_forms' },
];
const COLORS = { navy: '082F70', panelNavy: '203E6B', blue: '2F5A9D', pale: 'DCE8F7', green: '18864B', orange: 'F4B183', brightOrange: 'FF5A00', grey: 'F2F3F6', white: 'FFFFFF', text: '111111', red: 'FF0000' };

const remarkText = (value) => {
  const html = String(value ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|div|li|h[1-6])\s*>/gi, '\n');
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  parsed.querySelectorAll('script, style').forEach((element) => element.remove());
  return (parsed.body.textContent || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};
const agingRemarks = (row, fallback) => [
  row.internal_note_entries?.at?.(-1)?.note,
  row.internal_note_entries?.at?.(-1)?.notes,
  row.internal_notes,
  row.follow_up_notes,
].map(remarkText).find(Boolean) || fallback;

const normalizeLocations = (value) => (Array.isArray(value) ? value : String(value || '').split(','))
  .map((item) => String(item).trim())
  .filter(Boolean);
const imageUrlToDataUri = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error('The Routes logo could not be loaded for the PowerPoint.');
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('The Routes logo could not be embedded in the PowerPoint.'));
    reader.readAsDataURL(blob);
  });
};
const getLocation = (row) => String(row?.branchLocationCode || row?.claimOwningBranch || row?.location_name || row?.reported_claim?.branchLocationCode || '').trim();
const getDate = (row) => {
  const value = row?.closed_datetime || row?.created_datetime || row?.reportDateTime || row?.report_date_time;
  if (!value) return null;
  const direct = new Date(value);
  if (!Number.isNaN(direct.getTime())) return direct;
  const match = String(value).match(/^(\d{2})-(\d{2})-(\d{4})/);
  return match ? new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1])) : null;
};
const getLossDate = (row) => {
  const value = row?.accidentDate
    || row?.date_of_loss
    || row?.accidentDateTime
    || row?.reported_claim?.accidentDate
    || row?.reported_claim?.date_of_loss
    || row?.reported_claim?.accidentDateTime;
  if (!value) return null;
  const direct = new Date(value);
  if (!Number.isNaN(direct.getTime())) return direct;
  const match = String(value).match(/^(\d{2})-(\d{2})-(\d{4})/);
  return match ? new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1])) : null;
};
const isClosed = (row) => Boolean(row?.closure_completed || row?.claim_status === 'Completed' || row?.claim_workflow_stage === 'closed');
const formatWeekDate = (value) => {
  const date = new Date(`${value}T12:00:00`);
  const day = date.getDate();
  const suffix = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
  return `${date.toLocaleDateString('en-US', { month: 'long' })} ${day}${suffix} ${date.getFullYear()}`;
};
const formatDate = formatWeekDate;
const ageDays = (row, endDate) => {
  const created = getDate(row);
  return created ? Math.max(0, Math.floor((endDate - created) / 86400000)) : 0;
};
const lossAgeDays = (row, asOfDate) => {
  const lossDate = getLossDate(row);
  return lossDate ? Math.max(0, Math.floor((asOfDate - lossDate) / 86400000)) : null;
};
const bucket = (days) => (days > 90 ? '90+' : days > 60 ? '60-90' : days > 30 ? '30-60' : '1-30');
const category = (row) => {
  const route = `${row.insurance_route || ''} ${row.renter_insurance_type || ''} ${row.customerInsuranceCarrier || ''}`.toLowerCase();
  if (String(row.party_at_fault || '').toLowerCase().includes('3rd')) return 'thirdParty';
  if (route.includes('ota')) return 'ota';
  if (route.includes('personal') || route.includes('credit card') || route.includes('creditcard')) return 'personal';
  return 'routes';
};

const addText = (slide, text, x, y, w, h, options = {}) => slide.addText(String(text ?? ''), {
  x, y, w, h, margin: 0.04, fontFace: 'Aptos', fontSize: 12, color: COLORS.text,
  valign: 'mid', breakLine: false, ...options,
});
const addSectionTitle = (slide, title, x, y, w) => {
  slide.addShape('rect', { x, y, w, h: 0.42, fill: { color: COLORS.panelNavy }, line: { color: 'A9D18E', pt: 0.6 } });
  addText(slide, title, x + 0.1, y, w - 0.2, 0.42, { bold: true, color: COLORS.white, fontSize: 14, align: 'center' });
};
const addTable = (slide, rows, x, y, w, h, widths) => {
  slide.addTable(rows, {
    x, y, w, h, colW: widths, border: { type: 'solid', color: 'AAB7C7', pt: 0.7 },
    margin: 0.04, fontFace: 'Aptos', fontSize: 11.5, color: COLORS.text,
    rowH: 0.28, valign: 'middle',
    bold: false,
    fill: COLORS.white,
    autoFit: false,
  });
};

// Keep every aging claim on the location slide. The table grows into the spare
// space first, then reduces its text and row height as the claim count rises.
const addAgingTable = (slide, rows, x, y, w, h, widths) => {
  let cursor = y;
  const dataRows = Math.max(1, rows.length - 1);
  const headerHeight = Math.min(0.34, h * 0.18);
  const rowHeight = (h - headerHeight) / dataRows;
  const fontSize = Math.max(4, Math.min(10, rowHeight * 28));
  const drawRow = (cells, height, header = false) => {
    const padding = Math.min(0.04, height * 0.12);
    let left = x;
    cells.forEach((text, index) => {
      slide.addShape('rect', {
        x: left, y: cursor, w: widths[index], h: height,
        fill: { color: header ? COLORS.blue : COLORS.white },
        line: { color: 'AAB7C7', pt: 0.7 },
      });
      addText(slide, text, left + 0.04, cursor + padding, widths[index] - 0.08, Math.max(0.01, height - padding * 2), {
        fontSize: header ? Math.max(6, fontSize) : fontSize,
        valign: 'top', fit: 'shrink', breakLine: false,
        bold: header, color: header ? COLORS.white : COLORS.text,
      });
      left += widths[index];
    });
    cursor += height;
  };
  drawRow(['Rental Agreement #', 'Remarks'], headerHeight, true);
  rows.slice(1).forEach((cells) => drawRow(cells, rowHeight));
};

const buildSlide = (pptx, location, records, fromDate, toDate, logoData) => {
  if (logoData) return buildReferenceSlide(pptx, location, records, fromDate, toDate, logoData);
  const slide = pptx.addSlide();
  slide.background = { color: COLORS.white };
  const from = new Date(`${fromDate}T00:00:00`);
  const to = new Date(`${toDate}T23:59:59`);
  const pending = records.filter((row) => row.stage !== 'closed' && (getDate(row) || from) <= to);
  const received = records.filter((row) => {
    const date = getDate(row);
    return date && date >= from && date <= to;
  });
  const aging = ['1-30', '30-60', '60-90', '90+'];
  const countAging = (items) => aging.map((label) => items.filter((row) => bucket(ageDays(row, to)) === label).length);
  const routes = pending.filter((row) => category(row) === 'routes');
  const personal = pending.filter((row) => category(row) === 'personal');
  const ota = pending.filter((row) => category(row) === 'ota');
  const thirdParty = pending.filter((row) => category(row) === 'thirdParty');
  const newThirdParty = received.filter((row) => category(row) === 'thirdParty');
  const completed = received.filter((row) => row.stage === 'closed');
  const numberCell = (value, color = COLORS.blue) => ({
    text: String(value).padStart(2, '0'),
    options: { bold: true, color, align: 'center' },
  });
  const agingCells = (items) => countAging(items).map((value, index) => numberCell(value, index >= 2 ? COLORS.red : COLORS.text));
  const tableRows = [
    [
      { text: 'Claim Status', options: { bold: true, color: COLORS.navy, fill: COLORS.orange, align: 'center' } },
      ...['Count', ...aging].map((text) => ({ text, options: { bold: true, color: COLORS.navy, fill: COLORS.orange, align: 'center' } })),
    ],
    [{ text: 'Pending Claims', options: { bold: true } }, numberCell(pending.length), ...agingCells(pending)],
    [{ text: 'Total Claims Received', options: { bold: true } }, numberCell(received.length), 'N/A', 'N/A', { text: 'N/A', options: { color: COLORS.brightOrange } }, { text: 'N/A', options: { color: COLORS.red } }],
    [{ text: 'Routes CDW', options: { bold: true } }, numberCell(routes.length), ...agingCells(routes)],
    [{ text: 'Renter Personal & CC\nInsurance', options: { bold: true } }, numberCell(personal.length), ...agingCells(personal)],
    [{ text: 'OTA Insurance', options: { bold: true } }, numberCell(ota.length), ...agingCells(ota)],
    [{ text: 'Pending Third Party\nClaims', options: { bold: true } }, numberCell(thirdParty.length), ...agingCells(thirdParty)],
    [{ text: 'New Third Party Claims', options: { bold: true } }, numberCell(newThirdParty.length), ...agingCells(newThirdParty)],
    [
      { text: 'COMPLETED', options: { bold: true, color: '385723', fill: 'E2F0D9' } },
      { text: String(completed.length), options: { bold: true, color: '385723', fill: 'E2F0D9', align: 'center' } },
      ...aging.map(() => ({ text: 'N/A', options: { bold: true, color: '385723', fill: 'E2F0D9', align: 'center' } })),
    ],
  ];

  slide.background = { color: COLORS.grey };
  slide.addShape('rect', { x: 0.07, y: 0.06, w: 13.19, h: 1.03, fill: { color: COLORS.navy }, line: { color: 'D9E2F3', pt: 1 } });
  slide.addShape('rect', { x: 0.35, y: 0.18, w: 2.45, h: 0.72, fill: { color: COLORS.white }, line: { color: COLORS.white } });
  slide.addImage({ path: routesLogo, x: 0.4, y: 0.2, w: 2.35, h: 0.66 });
  addText(slide, String(location).toUpperCase().startsWith('') ? location : location, 7.55, 0.17, 4.25, 0.37, { fontSize: 17, bold: true, color: COLORS.white, align: 'center' });
  addText(slide, `${formatDate(fromDate)} – ${formatDate(toDate)}`, 8.0, 0.5, 4.95, 0.22, { fontSize: 9.5, color: 'DCE8F7', align: 'right' });

  addSectionTitle(slide, 'Claims Status & Aging', 0.25, 1.1, 7.05);
  addTable(slide, tableRows, 0.25, 1.48, 7.05, 3.1, [2.65, 0.72, 0.92, 0.92, 0.92, 0.92]);

  const oldClaims = pending.filter((row) => ageDays(row, to) >= 90).sort((a, b) => ageDays(b, to) - ageDays(a, to));
  addSectionTitle(slide, `Aging 90+ (${oldClaims.length})`, 7.55, 1.1, 5.53);
  const oldRows = [[
    { text: 'Rental Agreement #', options: { bold: true, color: COLORS.white, fill: COLORS.navy } },
    { text: 'Remarks', options: { bold: true, color: COLORS.white, fill: COLORS.navy } },
  ], ...(oldClaims.map((row) => [
    row.rentalAgreementNumber || row.ra_number || `#${row._id}`,
    agingRemarks(row, `${ageDays(row, to)} days pending`),
  ]))];
  if (oldRows.length === 1) oldRows.push(['—', 'No claims aged 90+']);
  const agingTableHeight = Math.min(1.85, 1.65 + Math.max(0, oldRows.length - 5) * 0.04);
  const distributionY = 1.56 + agingTableHeight + 0.14;
  addAgingTable(slide, oldRows, 7.55, 1.56, 5.53, agingTableHeight, [1.85, 3.68]);

  addSectionTitle(slide, 'Weekly Claims Distribution', 7.55, distributionY, 5.53);
  const bars = [
    ['New Forms', received.filter((row) => row.stage === 'new').length, COLORS.blue],
    ['Processing', received.filter((row) => row.stage === 'processing').length, COLORS.orange],
    ['Pending Close', received.filter((row) => row.stage === 'pendingClose').length, COLORS.purple],
    ['Closed', received.filter((row) => row.stage === 'closed').length, COLORS.green],
  ];
  const max = Math.max(1, ...bars.map((item) => item[1]));
  bars.forEach(([label, value, color], index) => {
    const y = distributionY + 0.48 + index * 0.38;
    addText(slide, label, 7.7, y, 1.25, 0.22, { fontSize: 8.5 });
    slide.addShape('rect', { x: 8.95, y: y + 0.03, w: 3.45, h: 0.16, fill: { color: 'E9EEF5' }, line: { color: 'E9EEF5' } });
    if (value) slide.addShape('rect', { x: 8.95, y: y + 0.03, w: 3.45 * value / max, h: 0.16, fill: { color }, line: { color } });
    addText(slide, value, 12.48, y, 0.35, 0.22, { fontSize: 8.5, bold: true, align: 'right' });
  });

  const atBodyShop = pending.filter((row) => String(row.vehicle_location || '').toLowerCase().includes('body shop'));
  const shopCounts = atBodyShop.reduce((result, row) => {
    const shop = String(row.body_shop_name || 'Body shop not specified').trim();
    result[shop] = (result[shop] || 0) + 1;
    return result;
  }, {});
  addSectionTitle(slide, 'Operational Pointers', 0.25, 4.82, 7.05);
  addText(slide, `• Claims received during selected period: ${received.length}\n• Pending claims as of period end: ${pending.length}\n• Vehicles currently at body shops: ${atBodyShop.length}`, 0.42, 5.23, 3.55, 1.02, { fontSize: 10, valign: 'top' });
  addText(slide, Object.entries(shopCounts).length
    ? Object.entries(shopCounts).map(([shop, count]) => `• ${shop}: ${count}`).join('\n')
    : '• No vehicles currently recorded at a body shop', 4.05, 5.23, 3.05, 1.02, { fontSize: 9.5, valign: 'top' });

  addSectionTitle(slide, 'Review Analysis', 7.55, 5.25, 5.53);
  addText(slide, 'Expedia Review Analysis', 7.75, 5.7, 2.35, 0.24, { fontSize: 9.5, bold: true, color: COLORS.navy });
  addText(slide, 'No review data available', 7.75, 6.02, 2.35, 0.3, { fontSize: 8.5, color: '718096' });
  addText(slide, 'Booking.com Review Analysis', 10.35, 5.7, 2.35, 0.24, { fontSize: 9.5, bold: true, color: COLORS.navy });
  addText(slide, 'No review data available', 10.35, 6.02, 2.35, 0.3, { fontSize: 8.5, color: '718096' });
  slide.addShape('line', { x: 0.25, y: 7.18, w: 12.83, h: 0, line: { color: 'CCD6E3', pt: 0.8 } });
  addText(slide, `Generated ${new Date().toLocaleString()}`, 0.3, 7.22, 6, 0.16, { fontSize: 7, color: '7A8A9E' });
  addText(slide, 'Confidential – Internal Claims Review', 7, 7.22, 6, 0.16, { fontSize: 7, color: '7A8A9E', align: 'right' });
};

const buildReferenceSlide = (pptx, location, records, fromDate, toDate, logoData) => {
  const slide = pptx.addSlide();
  const from = new Date(`${fromDate}T00:00:00`);
  const to = new Date(`${toDate}T23:59:59`);
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const pending = records.filter((row) => row.stage !== 'closed' && (getLossDate(row) || from) <= to);
  const received = records.filter((row) => {
    const date = getLossDate(row);
    return date && date >= from && date <= to;
  });
  const aging = ['1-30', '30-60', '60-90', '90+'];
  const countAging = (items) => aging.map((label) => items.filter((row) => {
    const days = lossAgeDays(row, today);
    return days !== null && bucket(days) === label;
  }).length);
  const routes = pending.filter((row) => category(row) === 'routes');
  const personal = pending.filter((row) => category(row) === 'personal');
  const ota = pending.filter((row) => category(row) === 'ota');
  const thirdParty = pending.filter((row) => category(row) === 'thirdParty');
  const newThirdParty = received.filter((row) => category(row) === 'thirdParty');
  const completed = received.filter((row) => row.stage === 'closed');
  const countCell = (value, color = COLORS.blue) => ({
    text: String(value).padStart(2, '0'),
    options: { bold: true, color, align: 'center' },
  });
  const agingCells = (items) => countAging(items).map((value, index) => countCell(value, index >= 2 ? COLORS.red : COLORS.text));
  const tableRows = [
    [
      { text: 'Claim Status', options: { bold: true, color: COLORS.navy, fill: COLORS.orange, align: 'center' } },
      ...['Count', ...aging].map((text) => ({ text, options: { bold: true, color: COLORS.navy, fill: COLORS.orange, align: 'center' } })),
    ],
    [{ text: 'Pending Claims', options: { bold: true } }, countCell(pending.length), ...agingCells(pending)],
    [{ text: 'Total Claims Received', options: { bold: true } }, countCell(received.length), 'N/A', 'N/A', { text: 'N/A', options: { color: COLORS.brightOrange } }, { text: 'N/A', options: { color: COLORS.red } }],
    [{ text: 'Routes CDW', options: { bold: true } }, countCell(routes.length), ...agingCells(routes)],
    [{ text: 'Renter Personal & CC\nInsurance', options: { bold: true } }, countCell(personal.length), ...agingCells(personal)],
    [{ text: 'OTA Insurance', options: { bold: true } }, countCell(ota.length), ...agingCells(ota)],
    [{ text: 'Pending Third Party Claims', options: { bold: true } }, countCell(thirdParty.length), ...agingCells(thirdParty)],
    [{ text: 'New Third Party Claims', options: { bold: true } }, countCell(newThirdParty.length), ...agingCells(newThirdParty)],
    [
      { text: 'COMPLETED', options: { bold: true, color: '385723', fill: 'E2F0D9' } },
      { text: String(completed.length), options: { bold: true, color: '385723', fill: 'E2F0D9', align: 'center' } },
      ...aging.map(() => ({ text: 'N/A', options: { bold: true, color: '385723', fill: 'E2F0D9', align: 'center' } })),
    ],
  ];

  slide.background = { color: COLORS.grey };
  slide.addShape('rect', { x: 0.07, y: 0.06, w: 13.19, h: 1.03, fill: { color: COLORS.navy }, line: { color: 'D9E2F3', pt: 1 } });
  slide.addImage({ data: logoData, x: 0.73, y: 0.18, w: 1.8, h: 0.74 });
  addText(slide, String(location).toUpperCase().startsWith('') ? location : location, 7.05, 0.14, 5.25, 0.44, { fontSize: 24, bold: true, color: COLORS.white, align: 'center' });
  addText(slide, `Date of Loss: ${formatWeekDate(fromDate)} to ${formatWeekDate(toDate)}`, 7.05, 0.64, 5.25, 0.26, { fontSize: 12.5, color: COLORS.white, align: 'center' });
  addTable(slide, tableRows, 0.24, 1.32, 7.08, 3.7, [2.9, 0.78, 0.85, 0.85, 0.85, 0.85]);

  const oldClaims = pending
    .filter((row) => (lossAgeDays(row, today) ?? -1) > 90)
    .sort((a, b) => (lossAgeDays(b, today) ?? 0) - (lossAgeDays(a, today) ?? 0));
  addSectionTitle(slide, 'Aging (90+)', 7.58, 1.32, 5.45);
  const oldRows = [[
    { text: 'Rental Agreement #', options: { bold: true, color: COLORS.white, fill: COLORS.blue, align: 'center' } },
    { text: 'Remarks', options: { bold: true, color: COLORS.white, fill: COLORS.blue, align: 'center' } },
  ], ...oldClaims.map((row) => [
    row.rentalAgreementNumber || row.ra_number || `#${row._id}`,
    agingRemarks(row, `${lossAgeDays(row, today)} days since loss`),
  ])];
  if (oldRows.length === 1) oldRows.push(['—', 'No claims aged 90+']);
  const agingTableHeight = Math.min(2.75, 2.0 + Math.max(0, oldRows.length - 5) * 0.1);
  const distributionY = 1.8 + agingTableHeight + 0.18;
  const distributionPanelY = distributionY + 0.42;
  addAgingTable(slide, oldRows, 7.58, 1.8, 5.45, agingTableHeight, [1.92, 3.53]);

  addSectionTitle(slide, 'Weekly Claims Distribution', 7.58, distributionY, 5.45);
  slide.addShape('rect', {
    x: 7.58, y: distributionPanelY, w: 5.45, h: Math.max(1.15, 6.65 - distributionPanelY),
    fill: { color: COLORS.white },
    line: { color: 'D9D9D9', pt: 0.5 },
  });

  const atBodyShop = pending.filter((row) => String(row.vehicle_location || '').toLowerCase().includes('body shop'));
  const shopCounts = atBodyShop.reduce((result, row) => {
    const shop = String(row.body_shop_name || 'Body shop not specified').trim();
    result[shop] = (result[shop] || 0) + 1;
    return result;
  }, {});
  addText(slide, 'Pointers:', 0.2, 5.58, 1.4, 0.26, { fontSize: 12.5, bold: true, color: COLORS.panelNavy });
  addText(slide, '* No action required from the location.', 0.2, 5.86, 6.7, 0.28, { fontSize: 12.5, bold: true, color: COLORS.panelNavy });
  addText(slide, `Vehicles at Body Shops (${atBodyShop.length})`, 0.2, 6.25, 3.6, 0.28, { fontSize: 12.5, bold: true, underline: true, color: COLORS.text });
  addText(slide, Object.entries(shopCounts).length
    ? Object.entries(shopCounts).map(([shop, count]) => `• ${shop} (${count})`).join('\n')
    : '• None', 0.42, 6.54, 6.6, 0.42, { fontSize: 12, bold: true, valign: 'top' });
  slide.addShape('rect', { x: 0.07, y: 7.0, w: 10.45, h: 0.43, fill: { color: COLORS.navy }, line: { color: COLORS.navy } });
  slide.addShape('rect', { x: 10.5, y: 7.0, w: 2.76, h: 0.43, fill: { color: COLORS.brightOrange }, line: { color: COLORS.brightOrange } });
};

export default function WeeklyClaimsPptReport() {
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const user = useMemo(() => getLoggedInUser(), []);
  const assigned = useMemo(() => normalizeLocations(user?.selected_locations || user?.locations), [user]);
  const today = new Date();
  const weekAgo = new Date(today); weekAgo.setDate(today.getDate() - 6);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [fromDate, setFromDate] = useState(weekAgo.toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(today.toISOString().slice(0, 10));
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const requests = SOURCES.flatMap((source) => ['new', 'processing', 'closed'].map((stage) => ({ source, stage, url: source[stage] })));
      const responses = await Promise.all(requests.map((item) => axios.get(`${baseURL}${item.url}`, { params: getDateRangeParams(fromDate, toDate) })));
      setRecords(responses.flatMap((response, index) => {
        const request = requests[index];
        return (Array.isArray(response.data) ? response.data : []).map((row) => ({
          ...row,
          workflow: request.source.workflow,
          stage: request.stage === 'closed' ? (isClosed(row) ? 'closed' : 'pendingClose') : request.stage,
        }));
      }));
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Weekly claims data could not be loaded.');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [baseURL, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const scoped = useMemo(() => records.filter((row) => assigned.some((item) => item.toLowerCase() === getLocation(row).toLowerCase())), [assigned, records]);
  const locations = useMemo(() => [...new Set(scoped.map(getLocation).filter(Boolean))].sort(), [scoped]);
  const generate = async () => {
    if (!fromDate || !toDate || fromDate > toDate) { setError('Select a valid From and To date range.'); return; }
    const selected = selectedLocations.length ? selectedLocations.filter((name) => locations.includes(name)) : locations;
    if (!selected.length) { setError('No assigned location data is available.'); return; }
    setGenerating(true); setError('');
    try {
      const pptx = new PptxGenJS();
      pptx.layout = 'LAYOUT_WIDE';
      pptx.author = user?.userName || 'Routes Assurance';
      pptx.subject = 'Weekly Claims Update';
      pptx.title = `Weekly Claims Update ${fromDate} to ${toDate}`;
      // PptxGenJS 3.x does not XML-escape this metadata value.
      pptx.company = 'Routes Car and Truck Rentals';
      pptx.lang = 'en-US';
      pptx.theme = { headFontFace: 'Aptos Display', bodyFontFace: 'Aptos', lang: 'en-US' };
      const logoData = await imageUrlToDataUri(routesLogo);
      selected.forEach((name) => buildSlide(pptx, name, scoped.filter((row) => getLocation(row) === name), fromDate, toDate, logoData));
      await pptx.writeFile({ fileName: `Weekly_Claims_Update_${fromDate}_to_${toDate}.pptx` });
    } catch (generationError) {
      setError(generationError?.message || 'The PowerPoint could not be generated.');
    } finally { setGenerating(false); }
  };
  const labelSx = { display: 'block', mb: 0.75, ml: 0.25, fontSize: 13, fontWeight: 600, color: '#36577f' };

  return (
    <Box sx={{ ml: { md: '240px' }, mt: '64px', p: { xs: 1.5, sm: 2.5, lg: 4 }, minHeight: 'calc(100vh - 64px)', bgcolor: '#f5f7fb' }}>
      <Paper sx={{ p: { xs: 2, sm: 3, lg: 4 }, borderRadius: { xs: 2, md: 4 }, boxShadow: '0 12px 35px rgba(23,57,113,.07)' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: { lg: 'center' }, gap: 3, mb: 4 }}>
          <Box><Typography sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 800, color: '#123b76' }}>Weekly Claims PowerPoint</Typography><Typography color="text.secondary">Generate the weekly claims update presentation for your assigned locations.</Typography></Box>
          <Box sx={{ ml: { lg: 'auto' }, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Chip label={`${locations.length} assigned location${locations.length === 1 ? '' : 's'}`} color="primary" variant="outlined" />
            <Button variant="outlined" startIcon={<IoRefreshOutline />} onClick={load} disabled={loading}>Refresh</Button>
            <Button variant="contained" startIcon={<IoDownloadOutline />} onClick={generate} disabled={loading || generating}>{generating ? 'Generating…' : 'Generate PPT'}</Button>
          </Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr 1fr' }, gap: 2, p: 2.5, borderRadius: 2.5, bgcolor: '#f7f9fc', border: '1px solid #e7edf5' }}>
          <Box><Typography sx={labelSx}>Location</Typography><FormControl fullWidth size="small"><Select multiple value={selectedLocations.filter((name) => locations.includes(name))} onChange={(event) => setSelectedLocations(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)} displayEmpty renderValue={(selected) => selected.length ? selected.join(', ') : 'All assigned locations'}>{locations.map((item) => <MenuItem key={item} value={item}><Checkbox checked={selectedLocations.includes(item)} /><ListItemText primary={item} /></MenuItem>)}</Select></FormControl></Box>
          <Box><Typography component="label" htmlFor="ppt-from-date" sx={labelSx}>Date of Loss — From</Typography><TextField id="ppt-from-date" type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} size="small" fullWidth inputProps={{ max: toDate || undefined }} /></Box>
          <Box><Typography component="label" htmlFor="ppt-to-date" sx={labelSx}>Date of Loss — To</Typography><TextField id="ppt-to-date" type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} size="small" fullWidth inputProps={{ min: fromDate || undefined }} /></Box>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {loading && <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>}
        {!loading && !assigned.length && <Alert severity="info" sx={{ mt: 2 }}>No locations are assigned to your account.</Alert>}
        {!loading && assigned.length > 0 && <Alert severity="info" sx={{ mt: 2 }}>Select one or more locations. With none selected, all assigned locations are included, with one slide per location.</Alert>}
      </Paper>
    </Box>
  );
}
